import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { uiLang } from '@/i18n/lang';

const FEATURES = ['filters', 'details', 'compare'] as const;
type Feature = (typeof FEATURES)[number];

/**
 * MapTeaser — full-width showcase of the Italy interactive map.
 * Placed on the homepage between AtlasTeaser and MethodNote.
 *
 * Screenshot is locale-aware: /guide/screenshots/{lang}/map-overview.jpg.
 * Browser chrome is intentionally muted (warm grays) to stay within the
 * "Quiet Atlas" brand — no macOS traffic-light colours.
 */
export function MapTeaser() {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n.language);
  const lp = useLocalizedPath();
  const screenshotSrc = `/guide/screenshots/${lang}/map-overview.jpg`;

  return (
    <section
      id="italy-map"
      aria-labelledby="map-teaser-title"
      className="border-y border-ink-100 bg-paper-2/40"
    >
      <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
        {/* Eyebrow + heading */}
        <div className="max-w-2xl">
          <div className="flex items-center gap-3">
            <p className="font-mono text-[11px] uppercase tracking-wider text-terra-600">
              {t('home.map.eyebrow')}
            </p>
            <span className="rounded bg-ink-100 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-500">
              🇮🇹 Italy now
            </span>
          </div>
          <h2
            id="map-teaser-title"
            className="mt-2 font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl"
          >
            {t('home.map.title')}
          </h2>
          <p className="mt-3 max-w-xl text-base leading-relaxed text-ink-500">
            {t('home.map.sub')}
          </p>
        </div>

        {/* Browser-framed screenshot */}
        <div className="mt-10 overflow-hidden rounded-xl border border-ink-200 bg-paper shadow-md">
          {/* Minimal browser chrome */}
          <div className="flex items-center gap-2.5 border-b border-ink-100 bg-paper-2 px-4 py-2.5">
            {/* Window control dots — muted to match warm palette */}
            <div className="flex gap-1.5" aria-hidden="true">
              <span className="block h-2.5 w-2.5 rounded-full bg-ink-200" />
              <span className="block h-2.5 w-2.5 rounded-full bg-ink-200" />
              <span className="block h-2.5 w-2.5 rounded-full bg-ink-200" />
            </div>
            {/* Fake address bar */}
            <div className="mx-2 flex-1 overflow-hidden rounded bg-ink-100 px-3 py-1 font-mono text-[10px] text-ink-400">
              lodestar.place/it/map
            </div>
          </div>

          {/* Screenshot — clickable, but tabIndex=-1 so the Link below is
              the accessible entry point for keyboard users */}
          <Link to="/it/map" tabIndex={-1} aria-hidden="true">
            <img
              src={screenshotSrc}
              alt=""
              loading="lazy"
              width={960}
              height={540}
              className="block w-full object-cover"
            />
          </Link>
        </div>

        {/* Feature cards */}
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {FEATURES.map((feat: Feature) => (
            <div
              key={feat}
              className="rounded-lg border border-ink-100 bg-paper px-4 py-4"
            >
              <p className="font-display text-base font-medium italic text-ink-900">
                {t(`home.map.feat.${feat}.title`)}
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-500">
                {t(`home.map.feat.${feat}.body`)}
              </p>
            </div>
          ))}
        </div>

        {/* CTAs — primary points to the new world map so first-time users
            land on the 30-country overview; secondary jumps straight into
            the Italy interactive map for users who came back to it. */}
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Link
            to={lp('/map')}
            className="inline-flex items-center gap-2 rounded-md bg-terra-600 px-5 py-2.5 font-sans text-sm font-medium text-paper transition-colors hover:bg-terra-700 focus:outline-none focus:ring-2 focus:ring-terra-600/30 focus:ring-offset-2 focus:ring-offset-paper-2"
          >
            {t('home.map.ctaWorld', { defaultValue: 'Open the world map' })}
            <span aria-hidden="true" className="font-mono text-xs opacity-80">→</span>
          </Link>
          <Link
            to="/it/map"
            className="font-mono text-xs uppercase tracking-wider text-ink-500 hover:text-ink-900"
          >
            {t('home.map.cta')}
          </Link>
        </div>
      </div>
    </section>
  );
}
